import { FileText } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { CheckRow, Field } from '@/components/common/FormBits'
import { useMinutesTemplate, useSaveMinutesTemplate, type MinutesTemplate } from '../meetingFiles'
import { toastError } from '../ui'

const SECTIONS = ['showParticipants', 'showAgenda', 'showActions', 'showSignatures', 'showInternalNotes'] as const

/** Toplantı tutanağı şablonu: başlık, müşteri sürümü giriş metni, sürüm başına altbilgi ve bölümler. */
export function MinutesTemplateButton() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <FileText />
        {t('crm.minutesDoc.template')}
      </Button>
      {open ? <TemplateModal onClose={() => setOpen(false)} /> : null}
    </>
  )
}

function TemplateModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const q = useMinutesTemplate()
  return (
    <Modal open size="lg" onOpenChange={(v) => !v && onClose()} icon={<FileText />} title={t('crm.minutesDoc.template')} description={t('crm.minutesDoc.templateHint')}>
      {q.data ? <TemplateForm initial={q.data} onClose={onClose} /> : <Skeleton className="h-64" />}
    </Modal>
  )
}

function TemplateForm({ initial, onClose }: { initial: MinutesTemplate; onClose: () => void }) {
  const { t } = useTranslation()
  const save = useSaveMinutesTemplate()
  const [v, setV] = useState(initial)
  return (
    <div className="space-y-4">
      <Field id="mt-title" label={t('crm.minutesDoc.tplTitle')}>
        <Input id="mt-title" value={v.title} maxLength={120} onChange={(e) => setV({ ...v, title: e.target.value })} />
      </Field>
      <Field id="mt-intro" label={t('crm.minutesDoc.tplIntro')} hint={t('crm.minutesDoc.tplIntroHint')}>
        <Textarea id="mt-intro" rows={3} maxLength={2000} value={v.customerIntro} onChange={(e) => setV({ ...v, customerIntro: e.target.value })} />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="mt-if" label={t('crm.minutesDoc.tplInternalFooter')}>
          <Input id="mt-if" value={v.internalFooter} maxLength={300} onChange={(e) => setV({ ...v, internalFooter: e.target.value })} />
        </Field>
        <Field id="mt-cf" label={t('crm.minutesDoc.tplCustomerFooter')}>
          <Input id="mt-cf" value={v.customerFooter} maxLength={300} onChange={(e) => setV({ ...v, customerFooter: e.target.value })} />
        </Field>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {SECTIONS.map((k) => (
          <CheckRow key={k} checked={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.checked })}>
            {t(`crm.minutesDoc.sections.${k}`)}
          </CheckRow>
        ))}
      </div>
      <div className="flex justify-end gap-2 border-t pt-3">
        <Button variant="cancel" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          disabled={!v.title.trim() || save.isPending}
          onClick={() =>
            save.mutate(v, {
              onSuccess: () => {
                toast.success(t('crm.minutesDoc.templateSaved'))
                onClose()
              },
              onError: toastError,
            })
          }
        >
          {t('common.save')}
        </Button>
      </div>
    </div>
  )
}
